import { Exchange, OperationResult } from 'urql';
import {
  filter,
  fromArray,
  fromPromise,
  map,
  merge,
  mergeMap,
  pipe,
} from 'wonka';

import { mockHandlers } from './mockHandlers';
import { MockHandler, MockHandlers } from './utils/mockUtils';
import { getOperationName, shouldMockOperation } from './utils/operationUtils';

// Generic mock exchange that can handle any GraphQL operation
export const mockExchange = (customHandlers: MockHandlers = {}): Exchange => {
  // Merge default handlers with custom handlers (custom handlers take precedence)
  const allHandlers: MockHandlers = { ...mockHandlers, ...customHandlers };

  return ({ forward }) => {
    return (operations$) => {
      // Split the stream into mocked operations and others
      const mockedOps$ = pipe(
        operations$,
        filter((op) => shouldMockOperation(op, allHandlers)),
      );

      const otherOps$ = pipe(
        operations$,
        filter((op) => !shouldMockOperation(op, allHandlers)),
      );

      // Handle mocked operations
      const mockedResults$ = pipe(
        mockedOps$,
        mergeMap((operation) => {
          const operationName = getOperationName(operation);
          const handler: MockHandler | undefined = operationName
            ? allHandlers[operationName]
            : undefined;

          if (!handler) {
            // This shouldn't happen due to our filtering, but just in case
            console.warn(
              `[MockExchange] No handler found for operation: ${operationName}`,
            );
            return pipe(fromArray([operation]), forward);
          }

          // Execute the handler
          const handlerResult: unknown = handler(operation);

          // Check if handler returns a promise
          if (handlerResult instanceof Promise) {
            return pipe(
              fromPromise(handlerResult),
              map((data: unknown) => {
                console.log(
                  `[MockExchange] Mock exchange handled ${operationName}:`,
                  data,
                );
                return {
                  operation,
                  data,
                  error: undefined,
                  extensions: undefined,
                  stale: false,
                  hasNext: false,
                } as OperationResult;
              }),
            );
          }

          // Synchronous handler
          console.log(
            `[MockExchange] Mock exchange handled ${operationName}:`,
            handlerResult,
          );
          return fromArray([
            {
              operation,
              data: handlerResult,
              error: undefined,
              extensions: undefined,
              stale: false,
              hasNext: false,
            } as OperationResult,
          ]);
        }),
      );

      // Forward all other operations normally
      const otherResults$ = pipe(otherOps$, forward);

      // Merge both streams
      return merge([mockedResults$, otherResults$]);
    };
  };
};

// Helper function to create a custom mock exchange with specific handlers
export const createMockExchange = (handlers: MockHandlers): Exchange => {
  console.log('[MockExchange] Creating custom mock exchange');

  return mockExchange(handlers);
};

// Helper function to add handlers at runtime (creates new exchange instance)
export const withMockHandlers = (handlers: MockHandlers): Exchange => {
  return mockExchange(handlers);
};
